import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Button, Box, Avatar, Menu, MenuItem,
  IconButton, Chip, Divider
} from '@mui/material';
import {
  Dashboard as AdminIcon,
  School as TrainerIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';

const DashboardSwitcher = ({ title = 'Admin Panel', userName }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);

  const isAdminDashboard = location.pathname.startsWith('/admin/dashboard');
  const isTrainerDashboard = location.pathname.startsWith('/trainer');

  // Check if user has super admin role
  const user = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const isSuperAdmin = user.role === 'super_admin' || user.role === 'admin';

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/auth');
  };

  const switchToAdmin = () => navigate('/admin/dashboard');
  const switchToTrainer = () => navigate('/trainer');

  const displayName = userName || user.fullName || user.username || 'User';
  const initials = displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <AppBar position="static" elevation={1} sx={{ bgcolor: 'primary.dark' }}>
      <Toolbar sx={{ gap: 1 }}>
        {/* Title / Brand */}
        <Typography variant="h6" sx={{ flexGrow: 0, mr: 2, fontWeight: 600 }}>
          {title}
        </Typography>

        {/* Current dashboard indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
          {isAdminDashboard && (
            <Chip icon={<AdminIcon />} label="Admin Dashboard" size="small" color="secondary" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }} />
          )}
          {isTrainerDashboard && (
            <Chip icon={<TrainerIcon />} label="Trainer Dashboard" size="small" color="warning" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }} />
          )}

          {/* Switch dashboard button (for super admin) */}
          {isSuperAdmin && (
            <Box sx={{ ml: 1 }}>
              {isAdminDashboard ? (
                <Button size="small" color="inherit" startIcon={<TrainerIcon />} onClick={switchToTrainer}
                  sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: 2, ml: 1 }}>
                  Switch to Trainer
                </Button>
              ) : (
                <Button size="small" color="inherit" startIcon={<AdminIcon />} onClick={switchToAdmin}
                  sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: 2, ml: 1 }}>
                  Switch to Admin
                </Button>
              )}
            </Box>
          )}
        </Box>

        {/* User menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
            {displayName}
          </Typography>
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: 14 }}>
              {initials}
            </Avatar>
          </IconButton>
        </Box>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Signed in as <strong>{displayName}</strong>
            </Typography>
          </MenuItem>
          <Divider />
          {isSuperAdmin && (
            <Box>
              <MenuItem onClick={() => { setAnchorEl(null); switchToAdmin(); }} selected={isAdminDashboard}>
                <AdminIcon sx={{ mr: 1 }} fontSize="small" /> Admin Dashboard
              </MenuItem>
              <MenuItem onClick={() => { setAnchorEl(null); switchToTrainer(); }} selected={isTrainerDashboard}>
                <TrainerIcon sx={{ mr: 1 }} fontSize="small" /> Trainer Dashboard
              </MenuItem>
              <Divider />
            </Box>
          )}
          <MenuItem onClick={() => { setAnchorEl(null); handleLogout(); }}>
            <LogoutIcon sx={{ mr: 1 }} fontSize="small" /> Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default DashboardSwitcher;
